import { useState } from 'react';

import { Button, HStack, Input, Text } from '@squire/ui';

import { CheckIcon } from '@/components/_icons';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { formatPrice } from '@/utils/internationalizationUtils';

import { TipItem } from './TipItem';
import { styles } from './styles';
import type { TipSelectorProps } from './types';

// Max custom tip of $1,999.99 (199999 cents), mirroring water-on-web's
// useCurrencyInput default cap "due to Stripe limitations" (SQR-13867).
// Entry is whole-dollars only, so the effective typed ceiling is $1999.
const MAX_CUSTOM_TIP_CENTS = 199999;
const MAX_CUSTOM_TIP_DOLLARS = Math.floor(MAX_CUSTOM_TIP_CENTS / 100);

const TipSelector = (props: TipSelectorProps) => {
  const {
    tips,
    selectedTipAmount,
    selectedTipPercentage,
    onSelectTip,
    loading = false,
    inverted = false,
    currency = 'USD',
    variant = 'segmented',
    equalWidthChips = false,
  } = props;
  const { t } = useScreenTranslations('TipSelector');

  styles.useVariants({ inverted, variant });

  const uniqueTips = tips.filter(
    (tip, index, self) =>
      index === self.findIndex((item) => item.percentage === tip.percentage),
  );

  // Small totals round presets to the same amount, so a caller tracking the
  // percentage (null = custom) is trusted over amount matching (SQR-17145).
  const tracksPercentage = selectedTipPercentage !== undefined;
  const effectiveSelectedPercentage = tracksPercentage
    ? selectedTipPercentage
    : selectedTipAmount > 0
      ? uniqueTips.find((tip) => tip.amount === selectedTipAmount)?.percentage
      : undefined;

  const isCustomTip = selectedTipPercentage
    ? !tips.find((tip) => tip.percentage === selectedTipPercentage)
    : selectedTipAmount > 0 &&
      (tracksPercentage || !effectiveSelectedPercentage);

  const [isOtherValue, setIsOtherValue] = useState(false);
  const [otherTipValue, setOtherTipValue] = useState(
    isCustomTip ? `${selectedTipAmount / 100}` : '',
  );

  const handleConfirmOtherTip = () => {
    const amount = Number.isInteger(Number(otherTipValue))
      ? Number(otherTipValue) * 100
      : undefined;

    if (amount && (!isCustomTip || amount !== selectedTipAmount)) {
      onSelectTip({ amount, isCustom: true });
    }
    setIsOtherValue(false);
  };

  const handleChangeOtherTip = (text: string) => {
    const digitsOnly = text.replace(/[^\d]/g, '');
    if (digitsOnly === '') {
      setOtherTipValue('');
      return;
    }
    const clamped = Math.min(Number(digitsOnly), MAX_CUSTOM_TIP_DOLLARS);
    setOtherTipValue(`${clamped}`);
  };

  if (isOtherValue) {
    return (
      <Input
        autoFocus
        keyboardType="numeric"
        value={otherTipValue}
        onChangeText={handleChangeOtherTip}
        onBlur={handleConfirmOtherTip}
        containerStyle={styles.otherTipInput}
        style={styles.otherTipInputText}
        accessibilityLabel={t('accessibility.enterCustomTip')}
        accessibilityHint={t('accessibility.enterCustomTipHint', {
          value: otherTipValue
            ? formatPrice(Number(otherTipValue), { currency })
            : 'empty',
        })}
        leftIcon={
          <Text
            variant="mStrong"
            color={inverted ? 'textPrimary' : 'cartTextPrimary'}
            accessibilityLabel={t('accessibility.dollarSign')}
          >
            {getCurrencySymbol(currency)}
          </Text>
        }
        rightIcon={
          otherTipValue ? (
            <Button
              size="small"
              style={styles.confirmOtherTipButton}
              onPress={handleConfirmOtherTip}
              testID="confirm-button"
              accessibilityRole="button"
              accessibilityLabel={t('accessibility.confirmButton', {
                amount: otherTipValue
                  ? ` of ${formatPrice(Number(otherTipValue), { currency })}`
                  : '',
              })}
              accessibilityHint={
                otherTipValue
                  ? t('accessibility.confirmButtonHintWithAmount', {
                      amount: otherTipValue,
                    })
                  : t('accessibility.confirmButtonHintEmpty')
              }
            >
              <CheckIcon
                color={inverted ? 'iconPrimaryInverse' : 'cartTipSelectedText'}
                accessibilityLabel={t('accessibility.confirm')}
              />
            </Button>
          ) : null
        }
      />
    );
  }
  return (
    <HStack
      style={styles.tippingContainer}
      accessibilityLabel={t('accessibility.tipSelectionOptions')}
      accessibilityHint={t('accessibility.tipSelectionHint')}
    >
      {variant === 'chips' ? null : (
        <TipItem
          disabled={loading}
          inverted={inverted}
          onPress={() => onSelectTip()}
          selected={!selectedTipAmount && !effectiveSelectedPercentage}
          accessibilityLabel={t('accessibility.noTip')}
          accessibilityHint={t('accessibility.noTipHint')}
          testID="tip-none"
        >
          {t('none')}
        </TipItem>
      )}

      {uniqueTips.map((tip) => (
        <TipItem
          key={tip.percentage}
          disabled={loading}
          inverted={inverted}
          variant={variant}
          equalWidthChips={equalWidthChips}
          onPress={() => onSelectTip(tip)}
          selected={effectiveSelectedPercentage === tip.percentage}
          accessibilityLabel={t('accessibility.percentageTip', {
            percentage: tip.percentage,
          })}
          accessibilityHint={t('accessibility.percentageTipHint', {
            percentage: tip.percentage,
            amount: formatPrice(tip.amount / 100, {
              showCents: true,
              currency,
            }),
          })}
          testID={`tip-${tip.percentage}`}
        >
          {`${tip.percentage}%`}
        </TipItem>
      ))}

      <TipItem
        disabled={loading}
        inverted={inverted}
        variant={variant}
        isCustomSlot
        equalWidthChips={equalWidthChips}
        selected={!!isCustomTip}
        onPress={() => setIsOtherValue(true)}
        accessibilityLabel={
          isCustomTip
            ? t('accessibility.customTip', {
                amount: formatPrice(selectedTipAmount / 100, { currency }),
              })
            : t('accessibility.customTipAmount')
        }
        accessibilityHint={
          isCustomTip
            ? t('accessibility.customTipSelected')
            : t('accessibility.customTipSelect')
        }
        testID="tip-custom"
      >
        {isCustomTip
          ? formatPrice(selectedTipAmount / 100, { currency })
          : variant === 'chips'
            ? t('custom')
            : t('other')}
      </TipItem>
    </HStack>
  );
};

TipSelector.displayName = 'TipSelector';
export { TipSelector };
